import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Container,
  Divider,
  Link,
} from '@mui/material';
import { CourseData } from '../types/types';
import pyqData from '../data/pyq_links.json';

const Home: React.FC = () => {
  const [courseCode, setCourseCode] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const data: CourseData = pyqData;

  const handleSearch = () => {
    const trimmedCode = courseCode.trim().toUpperCase();
    if (!trimmedCode) {
      setError('Please enter a course code');
      return;
    }

    if (data[trimmedCode]) {
      navigate(`/course/${trimmedCode}`);
    } else {
      setError('Course not found. Please check the course code and try again.');
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          mt: 8,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2d2d 100%)',
            color: '#fff',
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: 'Fira Code, monospace',
              textAlign: 'center',
              color: '#00ff00',
              textShadow: '0 0 10px rgba(0, 255, 0, 0.5)',
            }}
          >
            {'>_'} RIP.DSPACE
          </Typography>

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#ff4444',
                mb: 1,
              }}
            >
              outside the CB subnet?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#ff4444',
                mb: 1,
              }}
            >
              can't access{' '}
              <Link
                href="https://i.postimg.cc/mZ1QgCGF/DSPACE.png"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#ff4444',
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'underline',
                    opacity: 0.8,
                  },
                }}
              >
                DSPACE
              </Link>
              ?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#00ff00',
                mb: 1,
              }}
            >
              don't worry.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#00ff00',
                mb: 1,
              }}
            >
              we ripped it.
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#00ff00',
                mb: 1,
              }}
            >
              access every pyq from here.
            </Typography>
          </Box>

          <Divider sx={{ width: '100%', mb: 4, borderColor: '#444' }} />

          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#888',
                mb: 2,
              }}
            >
              VIT's DSPACE repository, which stores previous year question papers, is only accessible from within the campus network (CB subnet).
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#888',
                mb: 2,
              }}
            >
              That's a problem. So we solved it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#888',
                mb: 2,
              }}
            >
              This project aggregates most of the available PYQs across courses into a simple interface you can access anytime — from anywhere.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Fira Code, monospace',
                color: '#888',
                mb: 0,
              }}
            >
              No VPN. No restrictions. Just the PYQs you need{' '}
              <Link
                href="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#00ff00',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                :)
              </Link>
            </Typography>
          </Box>

          <Box sx={{ width: '100%', mt: 0, mb: 2 }}>
            <TextField
              fullWidth
              label="Course Code (e.g., CSE2016)"
              variant="outlined"
              value={courseCode}
              onChange={(e) => {
                setCourseCode(e.target.value);
                setError('');
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              error={!!error}
              helperText={error}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#444',
                  },
                  '&:hover fieldset': {
                    borderColor: '#666',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#00ff00',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#888',
                },
                '& .MuiInputBase-input': {
                  color: '#fff',
                },
              }}
            />
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSearch}
            sx={{
              minWidth: 200,
              background: 'linear-gradient(45deg, #00ff00 30%, #00cc00 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #00cc00 30%, #009900 90%)',
              },
            }}
          >
            Search
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Home; 